import { useUnreadMessages } from '@/contexts/MessagesContext'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import {Compass, Home, LogIn, LogOut, Mail, Newspaper, UserRound} from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface BottomNavProps {
  onAuthOpen: () => void
}

function BottomNav({ onAuthOpen }: BottomNavProps) {
  const { isAuthenticated } = useAuth()
  const { unreadCount } = useUnreadMessages()
  const location = useLocation()
  const navigate = useNavigate()
  const unreadLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  const homeActive = location.pathname === '/'
  const discoverActive = location.pathname === '/discover'
  const feedActive = location.pathname === '/feed'
  const profileActive = location.pathname === '/dashboard'
  const messagesActive = location.pathname.startsWith('/messages')

  const handleProfile = () => {
    if (!isAuthenticated) {
      onAuthOpen()
      return
    }
    navigate('/dashboard')
  }

  const handleAuth = async () => {
    if (!isAuthenticated) {
      onAuthOpen()
      return
    }
    await lumi.auth.signOut()
    navigate('/')
  }

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 border-t-4 border-[#FFDE00] bg-black/95 backdrop-blur-xl"
      style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto grid max-w-lg grid-cols-6 gap-0.5 px-1 pt-2">
        <Link
          to="/"
          className={`flex flex-col items-center gap-1 rounded-2xl px-2 py-2 transition-all ${
            homeActive
              ? 'bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800] -translate-y-0.5'
              : 'text-white/70 active:bg-white/10'
          }`}
        >
          <Home className="h-5 w-5" />
          <span className="font-display text-[11px] font-extrabold leading-none">Home</span>
        </Link>

        <Link
          to="/discover"
          className={`flex flex-col items-center gap-1 rounded-2xl px-1 py-2 transition-all ${
            discoverActive
              ? 'bg-[#3B4CCA] text-white shadow-[0_3px_0_#24308a] -translate-y-0.5'
              : 'text-white/70 active:bg-white/10'
          }`}
        >
          <Compass className="h-5 w-5" />
          <span className="font-display text-[10px] font-extrabold leading-none">Discover</span>
        </Link>

        <Link
          to="/feed"
          className={`flex flex-col items-center gap-1 rounded-2xl px-1 py-2 transition-all ${
            feedActive
              ? 'bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800] -translate-y-0.5'
              : 'text-white/70 active:bg-white/10'
          }`}
        >
          <Newspaper className="h-5 w-5" />
          <span className="font-display text-[10px] font-extrabold leading-none">Newsfeed</span>
        </Link>

        <button
          type="button"
          onClick={() => {
            if (!isAuthenticated) {
              onAuthOpen()
              return
            }
            navigate('/messages')
          }}
          className={`relative flex flex-col items-center gap-1 rounded-2xl px-1 py-2 transition-all ${
            messagesActive
              ? 'bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800] -translate-y-0.5'
              : 'text-white/70 active:bg-white/10'
          }`}
          aria-label={unreadCount > 0 ? `Messages, ${unreadCount} unread` : 'Messages'}
        >
          <Mail className="h-5 w-5" />
          {unreadCount > 0 ? (
            <span className="absolute right-1 top-1 min-w-[16px] rounded-full bg-[#FF1A1A] px-1 text-center text-[9px] font-extrabold leading-[16px] text-white">
              {unreadLabel}
            </span>
          ) : null}
          <span className="font-display text-[10px] font-extrabold leading-none">Inbox</span>
        </button>

        <button
          type="button"
          onClick={handleProfile}
          className={`flex flex-col items-center gap-1 rounded-2xl px-1 py-2 transition-all ${
            profileActive
              ? 'bg-[#3B4CCA] text-white shadow-[0_3px_0_#2a3794] -translate-y-0.5'
              : 'text-white/70 active:bg-white/10'
          }`}
        >
          <UserRound className="h-5 w-5" />
          <span className="font-display text-[11px] font-extrabold leading-none">Profile</span>
        </button>

        <button
          type="button"
          onClick={handleAuth}
          className={`flex flex-col items-center gap-1 rounded-2xl px-2 py-2 transition-all ${
            isAuthenticated
              ? 'text-white/70 active:bg-white/10'
              : 'bg-[#FF1A1A] text-white shadow-[0_3px_0_#b31212] -translate-y-0.5'
          }`}
        >
          {isAuthenticated ? <LogOut className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}
          <span className="font-display text-[11px] font-extrabold leading-none">
            {isAuthenticated ? 'Sign out' : 'Login'}
          </span>
        </button>
      </div>
    </nav>
  )
}

export default BottomNav
